// =============================================================================
// Engine client factory — typed access to the Atlas Onsite engine.
// =============================================================================
// `createEngineClient(config)` returns a strongly-typed handle that the
// React layer (via `EngineClientProvider`) hands to consumer hooks. Every
// path comes from `runtimeConfig.engine.*` — no module-level URL constants,
// no env reads. Different tenants in the same browser tab can re-instantiate
// safely; the dedup cache resets per call.
//
// Behaviour:
//   - `globalThis.fetch` + `AbortController` (caller can pass an outer signal)
//   - Sends `${apiKeyHeader}: ${publicApiKey}` on every request
//   - Path interpolation via `interpolatePath(template, params)`
//   - In-process dedup: simultaneous calls for the same URL share one promise
//   - Locale appended as `?lang=<bcp47>` only on endpoints the engine accepts
//     it on today (article, video, video-by-match). Feed paths do NOT yet
//     accept `?lang` (TODO: engine should add it once multilang lands).
//   - Errors thrown as `EngineFetchError` carrying `status`, `path`, `cause`.
//
// Hemmaklubben parity: the retry/timeout discipline (15s timeout, 2 retries
// with exponential backoff for 5xx + 429) matches the reference client. We
// keep that behaviour because it's battle-tested against the same backend.
// =============================================================================

import type {
  EngineEndpoints,
  FeedResponse,
  PollResponse,
  MatchDetailResponse,
  MatchOdds,
  StandingsResponse,
  LeadersResponse,
  MatchTimelineResponse,
  FsMatchesResponse,
  TeamPageResponse,
  VideoPackage,
  Article,
} from '@aamp/shared-types';

// ----------------------------------------------------------------------------
// Errors
// ----------------------------------------------------------------------------

/**
 * Thrown when an engine call ends in a non-2xx response or a fetch failure.
 * Carries the resolved path (NOT the full URL — `baseUrl` is config) and the
 * HTTP status when one was received. `cause` is preserved when the underlying
 * error is a `DOMException` (abort/timeout) or a network failure.
 */
export class EngineFetchError extends Error {
  public override readonly name = 'EngineFetchError';
  public readonly status: number;
  public readonly path: string;
  public readonly retryAfterSec: number | undefined;

  constructor(
    message: string,
    options: {
      status: number;
      path: string;
      retryAfterSec?: number;
      cause?: unknown;
    },
  ) {
    super(message, options.cause !== undefined ? { cause: options.cause } : undefined);
    this.status = options.status;
    this.path = options.path;
    this.retryAfterSec = options.retryAfterSec;
  }
}

// ----------------------------------------------------------------------------
// Constants
// ----------------------------------------------------------------------------

const DEFAULT_TIMEOUT_MS = 15_000;
const MAX_RETRIES = 2;
const RETRY_BASE_MS = 400;

/**
 * Endpoints the engine currently supports the `?lang=<locale>` query param
 * on. Feed paths are excluded by design — the engine doesn't read `?lang`
 * there yet. Once multilang lands, expand this set; render code stays the
 * same. Tracked as a TODO on the engine side.
 */
const LANG_AWARE_PATH_PREFIXES = [
  '/article/',
  '/video/',
  '/video/by-match/',
] as const;

// ----------------------------------------------------------------------------
// Public types
// ----------------------------------------------------------------------------

export interface EngineFetchOptions {
  /** Caller-provided abort signal — composed with the per-call timeout. */
  signal?: AbortSignal;
  /** Per-call timeout override. Defaults to 15s. */
  timeoutMs?: number;
  /** Per-call retry override. Defaults to 2. */
  retries?: number;
}

export interface FetchFixturesOptions extends EngineFetchOptions {
  /** ISO `YYYY-MM-DD` — earliest kickoff to include. */
  from?: string;
  /** ISO `YYYY-MM-DD` — latest kickoff to include. */
  to?: string;
}

/**
 * #409 Phase 3 — shape returned by GET /feed/:slug/match/:eventId/market-layouts.
 * Engine-side reference: apps/onsite-engine/src/routes/feed.ts.
 *
 * `plan` is typed loose (`unknown`) because the LayoutPlan schema is
 * owned by the layout-builder service; the render's LayoutRenderer
 * does its own Zod validation. Keeping it loose here avoids the
 * shared-types pnpm cache trap.
 */
/**
 * Insights engine Phase 5 — language-neutral ingredients of one jewel. Mirror
 * of the engine's `FindingParts`; typed independently here to avoid the
 * shared-types pnpm cache trap. Render's insightPhrase builder composes the
 * localised sentence from these; the engine bakes in NO language.
 */
export interface MatchInsightParts {
  kind: string;
  subject: string;
  value: number;
  baseline: number | null;
  /** Which baseline `baseline` is (0163): null/absent = league-wide mean,
   *  else the subject's own standings third — picks the tier-scoped
   *  sentence template ("mid-table avg" instead of "league avg"). */
  baselineTier?: 'top' | 'middle' | 'bottom' | null;
  opponent: number | null;
  fieldPath: string;
  /** Operator-authored catalog label (Danish today) — last-resort fallback. */
  fieldLabel: string | null;
  dataType: string | null;
  rank: number | null;
  rankOf: number | null;
  higherIsBetter: boolean | null;
  sampleSize: number | null;
  /** Streak/recency gems ("N of last M") — render uses the count-of-n template. */
  eventKey?: string;
  count?: number;
  ofN?: number;
}

/**
 * Insights engine Phase 5 — one aggregated "jewel" for the match surface.
 * `parts` drives localised rendering; `rationale` (English) is the fallback
 * for legacy plans without parts; `marketId` deep-links to the source section.
 */
export interface MatchInsight {
  strength: number;
  mode: string;
  marketId: string;
  marketLabel: string;
  rationale: string;
  parts: MatchInsightParts | null;
}

export interface MarketLayoutsResponse {
  eventId: string;
  tenantSlug: string;
  marketLayouts: Array<{
    marketId: string;
    label: string;
    categoryKey: string;
    plan: unknown;
    source: string;
    generatedAt: string;
    promptVersion: string;
    /**
     * #409 Phase 5d-2 — strongest finding strength for this market on
     * this match (0..1). NULL when no scoring data available. Render
     * doesn't display this directly — it's diagnostic info surfaced in
     * DevTools + the admin Bet-markets page.
     */
    findingStrength?: number | null;
    findingRationale?: string | null;
    findingMode?: string | null;
    /** C2 — top-N upfront flag; absent (older engine) = featured. */
    featured?: boolean;
  }>;
  /**
   * Markets queued for AI compose. Pre-#478 this was `string[]`; render
   * had no way to show the section header for a not-yet-composed market
   * and fell back to the raw catalog id. The objects here ship the same
   * `label` + `categoryKey` that cached entries carry so the rail reads
   * identically from cold start — only the body is the skeleton.
   */
  pending: Array<{
    marketId: string;
    label: string;
    categoryKey: string;
  }>;
  /**
   * #409 Phase 5c — engine-shipped odds card per marketId. Each value is
   * a `VideoOddsStripMarket` (typed loose here to avoid the shared-types
   * pnpm cache trap; render-side adapter validates the shape). Absent
   * key for a marketId = no usable provider offer; render hides the odds
   * slot for that section without breaking the analyst write-up.
   */
  oddsByMarketId?: Record<string, unknown>;
  /** Anytime-goalscorer list (favourite first), or null/absent when the book
   *  prices no player props for the event. Rendered by PlayerPropsCard. */
  playerProps?: { players: Array<{ name: string; price: number }> } | null;
  /**
   * #409 Phase 5d-3 — which ordering mode the response was built with.
   * `'bettor_relevance'` (static popularity) or `'finding_strength'`
   * (data-mined). Render uses whatever array order engine ships.
   */
  orderMode?: 'bettor_relevance' | 'finding_strength';
  /**
   * Insights engine Phase 5 — top 2-3 match-level jewels aggregated across
   * markets, strongest first. Absent/empty on cold cache or pre-parts plans.
   * Render's MatchInsights widget composes the localised sentence per item.
   */
  topInsights?: MatchInsight[];
  errors: string[];
}

export interface EngineClient {
  /** Tenant slug being served. Used to interpolate `{slug}` in templates. */
  readonly slug: string;
  /** Locale used for `?lang` query on lang-aware endpoints. */
  readonly locale: string;

  fetchFeed(opts?: EngineFetchOptions): Promise<FeedResponse>;
  fetchPoll(opts?: EngineFetchOptions): Promise<PollResponse>;
  fetchMatchDetail(
    eventId: string,
    opts?: EngineFetchOptions & { marketFocus?: string },
  ): Promise<MatchDetailResponse>;
  /**
   * #409 Phase 3 — Cached LayoutPlans for the multi-market kamp-side.
   * Read-only; never triggers LLM. Cold cache returns marketLayouts:[]
   * + pending: top-N market ids; the render shows skeleton sections for
   * pending markets. Auto-warm + polling come in Phase 4.
   */
  fetchMarketLayouts(eventId: string, opts?: EngineFetchOptions): Promise<MarketLayoutsResponse>;
  /** Public read-only marketing variants + generated media (radio spots +
   *  avatar video) for one match — drives the public showcase page. */
  fetchMatchVariants(eventId: string, opts?: EngineFetchOptions): Promise<MatchVariantsResponse>;
  fetchOdds(eventId: string, opts?: EngineFetchOptions): Promise<MatchOdds>;
  fetchVideoByMatch(eventId: string, opts?: EngineFetchOptions): Promise<VideoPackage>;
  fetchVideoById(videoId: string, opts?: EngineFetchOptions): Promise<VideoPackage>;
  fetchArticleById(articleId: string, opts?: EngineFetchOptions): Promise<Article>;
  fetchFixtures(opts?: FetchFixturesOptions): Promise<FsMatchesResponse>;
  fetchStandings(competitionId: string, opts?: EngineFetchOptions & { scope?: string }): Promise<StandingsResponse>;
  /**
   * Per-team destination. `teamId` is the BARE numeric competitor id (the
   * form the route carries), not the vendor URN — see `teamPath` in the
   * runtime-config contract. No competitionId: the engine scopes it from the
   * Host header so a tenant can never be asked for another league's club.
   */
  fetchTeamPage(teamId: string, opts?: EngineFetchOptions): Promise<TeamPageResponse>;
  fetchLeaders(competitionId: string, opts?: EngineFetchOptions): Promise<LeadersResponse>;
  fetchTimeline(eventId: string, opts?: EngineFetchOptions): Promise<MatchTimelineResponse>;

  /**
   * Prose Stories — Phase E. Discovery endpoint: returns the top markets
   * the tenant exposes for this event, with a `built` flag per market so
   * render can show a play icon (cache hit, instant playback) vs. a
   * "generate" icon (first-user cold path, ~30-60s wait).
   */
  fetchProseStoriesAvailable(eventId: string, opts?: EngineFetchOptions): Promise<ProseStoriesAvailable>;
  /**
   * Prose Stories — Phase E. Request (or fetch cached) a single prose
   * Story. Engine returns the existing row if present, otherwise builds
   * a fresh one. Caller MUST be prepared for a 30-60s response on cold
   * path; cache hit is ~75ms.
   */
  requestProseStory(eventId: string, marketId: string, marketLabel?: string, opts?: EngineFetchOptions): Promise<ProseStoryPackageDTO>;

  /** Test/escape hatch — drops the in-process dedup cache. */
  _clearDedupCache(): void;
}

export interface MatchVariantsResponse {
  eventId: string;
  tenantSlug: string;
  variants: Array<Record<string, unknown>>;
  media?: {
    radioSpots: Array<{ durationMs: number; audioUrl: string; script: string | null }>;
    videoUrl: string | null;
  };
}

export interface ProseStoriesAvailable {
  eventId: string;
  enabled: boolean;
  markets: ProseStoryAvailableMarket[];
}

export interface ProseStoryAvailableMarket {
  marketId: string;
  marketLabel: string;
  categoryKey: string;
  widgetKeys: string[];
  built: boolean;
  /** Present only when built=true. Render uses these to skip POST and play directly. */
  contentPackageId?: number;
  audioUrl?: string;
  durationMs?: number;
}

export interface ProseStoryPackageDTO {
  storyId: string;
  contentPackageId: number;
  prose: string;
  audioUrl: string;
  durationMs: number;
  sections: Array<{
    index: number;
    text: string;
    widgetKey: string;
    startChar: number;
    endChar: number;
  }>;
  /** Cleaned TTS input text — same string ElevenLabs spoke. Used with
   *  `charTimestamps` to compute which char/word is being said at any
   *  audio.currentTime. The source of truth for the karaoke + section
   *  + widget transitions — comes directly from ElevenLabs, not engine. */
  speechText: string;
  /** Per-char alignment from ElevenLabs. One entry per character in
   *  speechText, with startMs/endMs offsets from audio start. Render
   *  uses these to trigger UI changes on the EXACT spoken moment. */
  charTimestamps: Array<{ char: string; startMs: number; endMs: number }>;
  /** Unified overlay timeline (same Overlay type avatar Stories use).
   *  Player passes through activeOverlaysInTimeline() and dispatches
   *  via the same OverlayRenderer — Stories and prose Stories share
   *  one execution path. Pre-Phase-E3-2 stories may have empty array. */
  overlayTimeline: Array<{
    id: string;
    type: 'widget' | 'odds-card' | 'editorial-headline';
    animInMs: number;
    animOutMs: number;
    position: 'bottom-sheet' | 'top-banner' | 'center' | 'lower-third' | 'header-pinned';
    animation?: 'slide' | 'fade' | 'scale' | 'flip';
    widget?: { template: string; data: Record<string, unknown> };
    odds?: unknown;
    headline?: unknown;
  }>;
}

export interface CreateEngineClientArgs {
  endpoints: EngineEndpoints;
  /** Tenant slug — substituted into `{slug}` placeholders. */
  slug: string;
  /** BCP-47 locale used on lang-aware endpoints. */
  locale: string;
  /**
   * Optional callback fired the first time a 401 response is received
   * for any endpoint. The host (`EngineClientProvider`) wires this to
   * the tenant config refresh hook so a rotated `publicApiKey` can
   * recover without a manual reload. The callback is fired AT MOST
   * ONCE per client instance — recreating the client (i.e. tenant
   * config arrives) re-arms it.
   */
  onAuthFailure?: () => void;
}

// ----------------------------------------------------------------------------
// Helpers
// ----------------------------------------------------------------------------

/**
 * Interpolate `{name}` placeholders in a path template. Each value is
 * URL-encoded. Throws if a placeholder is missing from `params`. Pure —
 * easy to unit-test once P12 lands.
 */
export function interpolatePath(
  template: string,
  params: Readonly<Record<string, string>>,
): string {
  return template.replace(/\{(\w+)\}/g, (_, name: string): string => {
    const value = params[name];
    if (value === undefined) {
      throw new Error(`interpolatePath: missing parameter "${name}" for template "${template}"`);
    }
    return encodeURIComponent(value);
  });
}

function isLangAware(path: string): boolean {
  return LANG_AWARE_PATH_PREFIXES.some((prefix) => path.startsWith(prefix));
}

function appendQuery(path: string, query: Readonly<Record<string, string>>): string {
  const entries = Object.entries(query);
  if (entries.length === 0) return path;
  const sep = path.includes('?') ? '&' : '?';
  const qs = entries
    .map(([k, v]) => `${encodeURIComponent(k)}=${encodeURIComponent(v)}`)
    .join('&');
  return `${path}${sep}${qs}`;
}

function dedupKeyFor(slug: string, path: string): string {
  // Tenant slug is part of the key (audit fix #466). The dedup Map is
  // per-instance, so today only ONE tenant uses any given instance —
  // but if a future preview/impersonation mode shares a client across
  // tenants (or a host bug causes that to happen accidentally), the
  // slug guarantees a non-allow-listed response can never satisfy a request
  // made under a different tenant context. Cheap defence in depth.
  //
  // Path already includes the query string; sort query params so callers
  // that pass identical filters in different orders share the same slot.
  const [base, query = ''] = path.split('?');
  const baseKey = `${slug}::${base ?? path}`;
  if (query.length === 0) return baseKey;
  const sortedQuery = query.split('&').sort().join('&');
  return `${baseKey}?${sortedQuery}`;
}

function sleep(ms: number, signal?: AbortSignal): Promise<void> {
  return new Promise((resolve, reject) => {
    const timer = setTimeout(resolve, ms);
    if (signal) {
      const onAbort = (): void => {
        clearTimeout(timer);
        reject(new DOMException('Aborted', 'AbortError'));
      };
      if (signal.aborted) {
        clearTimeout(timer);
        reject(new DOMException('Aborted', 'AbortError'));
        return;
      }
      signal.addEventListener('abort', onAbort, { once: true });
    }
  });
}

// ----------------------------------------------------------------------------
// Factory
// ----------------------------------------------------------------------------

export function createEngineClient(args: CreateEngineClientArgs): EngineClient {
  const { endpoints, slug, locale, onAuthFailure } = args;
  const baseUrl = endpoints.baseUrl.replace(/\/+$/, '');

  // Per-instance dedup cache. Two simultaneous identical calls share one
  // promise. Settling promises remove themselves so the cache stays bounded.
  const inflight = new Map<string, Promise<unknown>>();

  // 401 fan-out: a single client instance fires `onAuthFailure` at most
  // once. The host listener (TenantConfigProvider) is itself debounced.
  let authFailureFired = false;
  function notifyAuthFailure(): void {
    if (authFailureFired) return;
    authFailureFired = true;
    if (onAuthFailure) {
      try {
        onAuthFailure();
      } catch {
        // Don't let a buggy listener take down the request path.
      }
    }
  }

  async function performFetch<T>(path: string, opts: EngineFetchOptions): Promise<T> {
    const { signal, timeoutMs = DEFAULT_TIMEOUT_MS, retries = MAX_RETRIES } = opts;

    let lastError: unknown;

    for (let attempt = 0; attempt <= retries; attempt += 1) {
      const ctl = new AbortController();
      const timer = setTimeout(
        () => ctl.abort(new DOMException('Timeout', 'TimeoutError')),
        timeoutMs,
      );
      const onOuterAbort = (): void => {
        ctl.abort(signal?.reason ?? new DOMException('Aborted', 'AbortError'));
      };
      if (signal) {
        if (signal.aborted) {
          clearTimeout(timer);
          throw new DOMException('Aborted', 'AbortError');
        }
        signal.addEventListener('abort', onOuterAbort, { once: true });
      }

      try {
        const url = `${baseUrl}${path}`;
        const headers: Record<string, string> = {
          Accept: 'application/json',
          [endpoints.apiKeyHeader]: endpoints.publicApiKey,
        };

        const res = await globalThis.fetch(url, {
          headers,
          signal: ctl.signal,
        });

        if (res.status === 401) {
          // Fire the auth-failure callback before throwing so the host
          // can kick off a tenant-config refresh. The throw still happens
          // — this request fails — but the next mount with a refreshed
          // `publicApiKey` will succeed without a page reload.
          notifyAuthFailure();
          throw new EngineFetchError('Unauthorized — invalid public API key', {
            status: 401,
            path,
          });
        }
        if (res.status === 404) {
          throw new EngineFetchError('Not found', { status: 404, path });
        }
        if (res.status === 429) {
          const retryAfter = Number(res.headers.get('Retry-After')) || undefined;
          if (attempt < retries) {
            const waitMs = (retryAfter ?? 2 ** attempt) * 1000;
            await sleep(waitMs, signal);
            continue;
          }
          throw new EngineFetchError('Rate limited', {
            status: 429,
            path,
            ...(retryAfter !== undefined ? { retryAfterSec: retryAfter } : {}),
          });
        }
        if (res.status >= 500 && attempt < retries) {
          await sleep(RETRY_BASE_MS * 2 ** attempt, signal);
          continue;
        }
        if (!res.ok) {
          throw new EngineFetchError(`HTTP ${res.status}`, {
            status: res.status,
            path,
          });
        }

        const data = (await res.json()) as T;
        return data;
      } catch (err) {
        // Aborts/timeouts never retry — surface immediately.
        if (err instanceof DOMException && (err.name === 'AbortError' || err.name === 'TimeoutError')) {
          throw err;
        }
        // EngineFetchError from a definitive status (401/404/429-final) — surface.
        if (err instanceof EngineFetchError && err.status !== 429 && err.status < 500) {
          throw err;
        }
        lastError = err;
        if (attempt < retries) {
          await sleep(RETRY_BASE_MS * 2 ** attempt, signal);
          continue;
        }
        if (err instanceof EngineFetchError) throw err;
        throw new EngineFetchError(
          err instanceof Error ? err.message : 'Network error',
          { status: 0, path, cause: err },
        );
      } finally {
        clearTimeout(timer);
        if (signal) signal.removeEventListener('abort', onOuterAbort);
      }
    }

    if (lastError instanceof Error) throw lastError;
    throw new EngineFetchError('Unknown error', { status: 0, path });
  }

  async function get<T>(rawPath: string, opts?: EngineFetchOptions): Promise<T> {
    const path = isLangAware(rawPath) ? appendQuery(rawPath, { lang: locale }) : rawPath;
    const key = dedupKeyFor(slug, path);
    const existing = inflight.get(key) as Promise<T> | undefined;
    if (existing) return existing;

    const promise = performFetch<T>(path, opts ?? {})
      .finally(() => {
        inflight.delete(key);
      });
    inflight.set(key, promise);
    return promise;
  }

  // ----- public methods --------------------------------------------------

  function fetchFeed(opts?: EngineFetchOptions): Promise<FeedResponse> {
    const path = interpolatePath(endpoints.feedPath, { slug });
    return get<FeedResponse>(path, opts);
  }

  function fetchPoll(opts?: EngineFetchOptions): Promise<PollResponse> {
    const path = interpolatePath(endpoints.feedPollPath, { slug });
    return get<PollResponse>(path, opts);
  }

  function fetchMatchDetail(
    eventId: string,
    opts?: EngineFetchOptions & { marketFocus?: string },
  ): Promise<MatchDetailResponse> {
    const path = interpolatePath(endpoints.matchDetailPath, { slug, eventId });
    // Phase 5G — when the caller passes a marketFocus, append it as a
    // query parameter. The engine reads ?market=X to decide whether to
    // enrich the response with `marketLayout` (LayoutPlan). Absent =>
    // bundle-only path (existing behaviour, unchanged).
    const finalPath = opts?.marketFocus
      ? appendQuery(path, { market: opts.marketFocus })
      : path;
    return get<MatchDetailResponse>(finalPath, opts);
  }

  /**
   * #409 Phase 3 — Read-only fetch of cached per-market LayoutPlans for
   * the multi-market kamp-side. Engine returns
   *   { marketLayouts: [{marketId, label, categoryKey, plan, source,
   *                       generatedAt, promptVersion}],
   *     pending: [marketId,…],
   *     errors: [marketId,…] }
   * Pure cache read — never calls Haiku. Cold cache => marketLayouts:[]
   * + pending populated; render shows skeleton sections for the pending
   * markets.
   *
   * We construct the path off `matchDetailPath` (which is
   * `/feed/{slug}/match/{eventId}`) rather than adding a new field to
   * EngineEndpoints. The endpoint shipped post-contract and the
   * shared-types pnpm cache trap (see CLAUDE.md) makes contract
   * changes the most expensive path. Sibling-of-existing-path is the
   * cleanest workaround.
   */
  function fetchMarketLayouts(
    eventId: string,
    opts?: EngineFetchOptions,
  ): Promise<MarketLayoutsResponse> {
    const basePath = interpolatePath(endpoints.matchDetailPath, { slug, eventId });
    const path = `${basePath}/market-layouts`;
    return get<MarketLayoutsResponse>(path, opts);
  }

  // Public marketing variants + generated media. Sibling of matchDetailPath
  // (`/feed/{slug}/match/{eventId}/variants`) — reuses the correct baseUrl +
  // public API key the working hooks use (the hardcoded /tenant-api path the
  // old public screen used 404'd; feed routes are mounted without that prefix).
  function fetchMatchVariants(eventId: string, opts?: EngineFetchOptions): Promise<MatchVariantsResponse> {
    const basePath = interpolatePath(endpoints.matchDetailPath, { slug, eventId });
    return get<MatchVariantsResponse>(`${basePath}/variants`, opts);
  }

  function fetchOdds(eventId: string, opts?: EngineFetchOptions): Promise<MatchOdds> {
    const path = interpolatePath(endpoints.oddsPath, { eventId });
    return get<MatchOdds>(path, opts);
  }

  function fetchVideoByMatch(
    eventId: string,
    opts?: EngineFetchOptions,
  ): Promise<VideoPackage> {
    const path = interpolatePath(endpoints.videoByMatchPath, { eventId });
    return get<VideoPackage>(path, opts);
  }

  function fetchVideoById(videoId: string, opts?: EngineFetchOptions): Promise<VideoPackage> {
    const path = interpolatePath(endpoints.videoPath, { videoId });
    return get<VideoPackage>(path, opts);
  }

  function fetchArticleById(articleId: string, opts?: EngineFetchOptions): Promise<Article> {
    const path = interpolatePath(endpoints.articlePath, { articleId });
    return get<Article>(path, opts);
  }

  function fetchFixtures(opts?: FetchFixturesOptions): Promise<FsMatchesResponse> {
    const query: Record<string, string> = {};
    if (opts?.from !== undefined) query['from'] = opts.from;
    if (opts?.to !== undefined) query['to'] = opts.to;
    const basePath = endpoints.fixturesPath;
    const path = appendQuery(basePath, query);
    const fetchOpts: EngineFetchOptions = {};
    if (opts?.signal !== undefined) fetchOpts.signal = opts.signal;
    if (opts?.timeoutMs !== undefined) fetchOpts.timeoutMs = opts.timeoutMs;
    if (opts?.retries !== undefined) fetchOpts.retries = opts.retries;
    return get<FsMatchesResponse>(path, fetchOpts);
  }

  function fetchStandings(
    competitionId: string,
    opts?: EngineFetchOptions & { scope?: string },
  ): Promise<StandingsResponse> {
    const basePath = interpolatePath(endpoints.standingsPath, { competitionId });
    const path = opts?.scope
      ? `${basePath}?scope=${encodeURIComponent(opts.scope)}`
      : basePath;
    // Wire shape is `{ competitionId, standings }` (legacy public
    // contract, kept stable) — normalize to the domain StandingsResponse
    // here so hooks read `.rows`. Caught live 2026-08-01: the hook was
    // written against `.rows` before any consumer existed and the drift
    // shipped a crash with the first real consumer (/standings page).
    return get<{
      competitionId?: string;
      standings?: StandingsResponse['rows'];
      rows?: StandingsResponse['rows'];
    }>(path, opts).then((raw) => ({
      leagueId: raw.competitionId ?? competitionId,
      leagueName: '',
      rows: raw.rows ?? raw.standings ?? [],
    }));
  }

  function fetchTeamPage(
    teamId: string,
    opts?: EngineFetchOptions,
  ): Promise<TeamPageResponse> {
    // No wire-shape normalisation here, deliberately: the engine ships
    // `TeamPageResponse` verbatim. `fetchStandings` above needs a translation
    // layer only because its wire contract predates the domain type and had
    // to stay stable; a new endpoint gets to have one shape.
    const path = interpolatePath(endpoints.teamPath, { teamId });
    return get<TeamPageResponse>(path, opts);
  }

  function fetchLeaders(
    competitionId: string,
    opts?: EngineFetchOptions,
  ): Promise<LeadersResponse> {
    const path = interpolatePath(endpoints.leadersPath, { competitionId });
    // Wire shape is `{ competitionId, lists: [{type, leaders:[{player,
    // team}]}] }` — normalize to LeadersResponse boards (same category
    // inference as the engine provider) so hooks read `.boards`.
    interface WireLeader {
      rank?: number;
      value?: number;
      player?: { id?: string; name?: string };
      team?: { id?: string; name?: string };
    }
    interface WireList {
      type?: string;
      leaders?: WireLeader[];
    }
    return get<{ lists?: WireList[]; boards?: LeadersResponse['boards'] }>(path, opts).then(
      (raw) => {
        if (raw.boards) return { leagueId: competitionId, boards: raw.boards };
        const boards: LeadersResponse['boards'] = [];
        for (const list of raw.lists ?? []) {
          const type = (list.type ?? '').toLowerCase();
          const category =
            type.includes('goal') && !type.includes('own')
              ? ('goals' as const)
              : type.includes('assist')
                ? ('assists' as const)
                : type.includes('yellow')
                  ? ('yellow_cards' as const)
                  : type.includes('red')
                    ? ('red_cards' as const)
                    : type.includes('own')
                      ? ('own_goals' as const)
                      : null;
          if (category === null) continue;
          boards.push({
            category,
            title: list.type ?? category,
            emoji: '',
            leaders: (list.leaders ?? []).map((l) => ({
              rank: l.rank ?? 0,
              playerId: l.player?.id ?? '',
              playerName: l.player?.name ?? '',
              teamId: l.team?.id ?? '',
              teamName: l.team?.name ?? '',
              teamAbbreviation: '',
              value: l.value ?? 0,
              source: 'leaders',
            })),
          });
        }
        return { leagueId: competitionId, boards };
      },
    );
  }

  function fetchTimeline(
    eventId: string,
    opts?: EngineFetchOptions,
  ): Promise<MatchTimelineResponse> {
    const path = interpolatePath(endpoints.timelinePath, { eventId });
    return get<MatchTimelineResponse>(path, opts);
  }

  // ---------------------------------------------------------------------------
  // Prose Stories (Phase E) — discovery + on-demand request.
  //
  // These two methods use simple direct fetch instead of the dedup'd
  // get<T>() helper because:
  //   - POST /story has 30-60s cold-path latency; we don't want it sharing
  //     a dedup slot with anything else.
  //   - The endpoint paths are hardcoded as `/onsite/insight/...` since
  //     they post-date EngineEndpoints contract. Future cleanup: lift
  //     them into the contract like the other paths.
  //   - No apiKeyHeader is required by the engine for these routes (they
  //     resolve the tenant from Host header only) — same posture as
  //     /feed/:tenant/match/:eventId.
  // ---------------------------------------------------------------------------
  async function rawJsonFetch<T>(
    method: 'GET' | 'POST',
    path: string,
    body: unknown,
    opts: EngineFetchOptions,
  ): Promise<T> {
    const { signal, timeoutMs = DEFAULT_TIMEOUT_MS } = opts;
    const ctl = new AbortController();
    const timer = setTimeout(
      () => ctl.abort(new DOMException('Timeout', 'TimeoutError')),
      timeoutMs,
    );
    const onOuterAbort = (): void => {
      ctl.abort(signal?.reason ?? new DOMException('Aborted', 'AbortError'));
    };
    if (signal) {
      if (signal.aborted) {
        clearTimeout(timer);
        throw new DOMException('Aborted', 'AbortError');
      }
      signal.addEventListener('abort', onOuterAbort, { once: true });
    }
    try {
      const url = `${baseUrl}${path}`;
      const headers: Record<string, string> = {
        Accept: 'application/json',
        [endpoints.apiKeyHeader]: endpoints.publicApiKey,
      };
      if (body !== undefined) headers['Content-Type'] = 'application/json';
      const res = await globalThis.fetch(url, {
        method,
        headers,
        signal: ctl.signal,
        ...(body !== undefined ? { body: JSON.stringify(body) } : {}),
      });
      if (!res.ok) {
        throw new EngineFetchError(`HTTP ${res.status}`, { status: res.status, path });
      }
      return (await res.json()) as T;
    } finally {
      clearTimeout(timer);
      if (signal) signal.removeEventListener('abort', onOuterAbort);
    }
  }

  function fetchProseStoriesAvailable(
    eventId: string,
    opts?: EngineFetchOptions,
  ): Promise<ProseStoriesAvailable> {
    // baseUrl on staging is `/onsite` (relative, Caddy-proxied), on prod
    // it'll be `https://api.<domain>`. Either way the engine route is
    // `/insight/:eventId/available` — the baseUrl carries the prefix.
    const path = `/insight/${encodeURIComponent(eventId)}/available`;
    // 5s timeout: discovery is a fast DB lookup, no LLM/TTS work.
    return rawJsonFetch<ProseStoriesAvailable>('GET', path, undefined, {
      timeoutMs: 5000,
      ...(opts ?? {}),
    });
  }

  function requestProseStory(
    eventId: string,
    marketId: string,
    marketLabel?: string,
    opts?: EngineFetchOptions,
  ): Promise<ProseStoryPackageDTO> {
    const path = `/insight/${encodeURIComponent(eventId)}/story`;
    const body = marketLabel !== undefined
      ? { marketId, marketLabel }
      : { marketId };
    // 90s timeout: cold path is Sonnet (~10s) + Haiku (~5s) + ElevenLabs
    // TTS (~30-60s) + S3 upload + DB insert. Cache hit is ~75ms. The 90s
    // ceiling covers a slow-but-not-broken cold path; longer = render
    // shows an error and offers retry.
    return rawJsonFetch<ProseStoryPackageDTO>('POST', path, body, {
      timeoutMs: 90_000,
      ...(opts ?? {}),
    });
  }

  function _clearDedupCache(): void {
    inflight.clear();
  }

  return {
    slug,
    locale,
    fetchFeed,
    fetchPoll,
    fetchMatchDetail,
    fetchMarketLayouts,
    fetchMatchVariants,
    fetchProseStoriesAvailable,
    requestProseStory,
    fetchOdds,
    fetchVideoByMatch,
    fetchVideoById,
    fetchArticleById,
    fetchFixtures,
    fetchStandings,
    fetchTeamPage,
    fetchLeaders,
    fetchTimeline,
    _clearDedupCache,
  };
}
